// src/utils/continueJobUtils.js
// ===========================================================================
// "Continue" a job WITHOUT completing it.
//
// The Home page's Continue button does two things: it completes the current
// job and it creates a copy of that job on a new date under a stemmed work
// order (5456 -> 5456-2 -> 5456-3). On the invoice pages the original job is
// finished and often already invoiced or paid, so completing it again makes no
// sense. Everything here does the SECOND half only: a new Active job that
// stems off the same work order number, with the original job left exactly as
// it is — no status change, no completed date, no write of any kind.
//
// The copy is deliberately stripped of anything that belongs to the original
// run of the job: its notes, receipts, completed dates, totals and every
// invoice/paid flag. Carrying those over would make a brand new job show up as
// already invoiced.
// ===========================================================================

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { getMaterialsGroupId } from "./materialsUtils";

/** "5456-3" -> "5456". "5456" -> "5456". */
export const getWOStem = (wo) => String(wo ?? "").trim().replace(/-\d+$/, "");

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const dateKey = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  if (typeof value === "string") return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

/*
  Fields that must NOT ride along on the new job.

  Anything that records what happened on the ORIGINAL job: its history, its
  money, and its place in the invoice pipeline. Keeping the shape of the work
  (site, scope, description, tech, property, proposal link) is the whole point
  of a continuation, so those stay.
*/
const FIELDS_NOT_COPIED = [
  // record-keeping, not job data
  "id",
  "__collection",
  "__renderDate",
  "occurrenceDate",
  "newFiles",
  "creationDate",
  "updatedAt",
  // the original run's history
  "notes",
  "receipts",
  "receiptsTotal",
  "completedDates",
  "completedAt",
  "completedBy",
  "occurrenceNotes",
  // money already counted against the original
  "estimatedTotal",
  "jobTotal",
  "total",
  "depositPrice",
  "depositSent",
  // invoice pipeline flags - a new job has not been invoiced or paid
  "approvedInvoice",
  "approvedInvoiceId",
  "approvedInvoiceAt",
  "invoiceSent",
  "invoiceSentDate",
  "invoicedDates",
  "invoiceNumber",
  "invoiceId",
  "invoiceSentFromApprovedInvoiceId",
  "lastInvoiceSentAt",
  "invoiceSentReversedAt",
  "paid",
  "paidAt",
  "paidAmount",
  // links written on a job when IT was continued
  "continuedToDate",
  // Priority recurring-series bookkeeping and invoice links - none of it
  // belongs to a brand new one-time job.
  "completedAtByDate",
  "occurrenceOverrides",
  "excludedDates",
  "originalOccurrenceDate",
  "occurrenceStatus",
  "__rowKey",
  "__occurrenceRow",
  "__seriesRow",
  "seriesSplitAfter",
  "seriesEndedAt",
  "invoiceStatus",
  "completedInvoiceData",
  "continuedToWO",
  "projectContinuedToDate",
  "projectContinuedToWO",
  "projectContinuedToJobId",
];

/**
 * Next free work order under this stem: 5456 -> 5456-2, 5456-2 -> 5456-3.
 *
 * The number comes from the HIGHEST suffix already in use, not from how many
 * related jobs exist. Counting breaks after a branch is deleted — three jobs
 * numbered 5456, 5456-2, 5456-4 would hand out 5456-4 a second time.
 */
export const getNextContinuedWO = async (baseWO) => {
  const stem = getWOStem(baseWO);
  if (!stem) return "";

  // Range scan over the stem. Branch numbers always contain a dash, so they
  // are always strings and always land inside this range.
  const snap = await getDocs(
    query(
      collection(db, "jobs"),
      where("wo", ">=", stem),
      where("wo", "<=", `${stem}\uf8ff`)
    )
  );

  const taken = new Set();
  let highest = 1;

  snap.forEach((d) => {
    const wo = String(d.data()?.wo ?? "").trim();
    if (!wo) return;
    taken.add(wo);
    const match = wo.match(/^(.*)-(\d+)$/);
    if (match && match[1] === stem) {
      highest = Math.max(highest, Number(match[2]) || 0);
    }
  });

  let next = highest + 1;
  while (taken.has(`${stem}-${next}`) && next < highest + 100) next += 1;

  return `${stem}-${next}`;
};

/** The job behind an invoice row: by id first, then by work order. */
export const findJobForWO = async ({ jobId, originalJobId, wo }) => {
  const byId = jobId || originalJobId;

  if (byId) {
    const snap = await getDoc(doc(db, "jobs", byId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  }

  const woValue = String(wo ?? "").trim();
  if (woValue) {
    const snap = await getDocs(
      query(collection(db, "jobs"), where("wo", "==", woValue))
    );
    if (!snap.empty) {
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }
  }

  return null;
};

/**
 * Creates the continuation job. The source job is READ ONLY here — nothing is
 * written back to it.
 *
 * Returns { id, job, wo } for the job that was created.
 */
export const createContinuationJob = async ({
  sourceJob,
  newDate,
  newWO,
  createdBy,
}) => {
  if (!sourceJob?.id) throw new Error("No source job to continue from.");

  const startDate = dateKey(newDate) || todayKey();
  const wo = newWO || (await getNextContinuedWO(sourceJob.wo));
  const fromDate = dateKey(sourceJob.startDate);

  const user = createdBy || (() => {
    const current = getAuth()?.currentUser;
    const fullName = current?.displayName || "";
    return {
      uid: current?.uid || "",
      firstName: fullName.split(" ")[0] || "",
      lastName: fullName.split(" ").slice(1).join(" ") || "",
      fullName: fullName || current?.email || "User",
    };
  })();

  const copiedJob = { ...sourceJob };
  FIELDS_NOT_COPIED.forEach((key) => {
    delete copiedJob[key];
  });

  // Materials are shared per group, so the copy points at the same group the
  // original used rather than starting empty.
  copiedJob.materialsGroupId = getMaterialsGroupId(sourceJob);

  if (copiedJob.jobPlan) {
    copiedJob.jobPlan = { ...copiedJob.jobPlan };
    delete copiedJob.jobPlan.materials;
  }

  const newJob = {
    ...copiedJob,

    wo: wo || sourceJob.wo || "",
    startDate,
    endDate: startDate,
    status: "Active",
    recurrence: { type: "none" },

    notes: [],
    receipts: [],
    receiptsTotal: 0,
    completedDates: [],
    occurrenceNotes: {},

    // where it came from. Record only - the two jobs are independent.
    continuedFromJobId: sourceJob.id,
    continuedFromWO: sourceJob.wo || "",
    continuedFromDate: fromDate || "",
    originalStartDate: fromDate || sourceJob.originalStartDate || "",

    createdByContinue: true,
    // Set apart from Home's Continue: that one closes the job it came from,
    // this one leaves it alone.
    continuedWithoutCompleting: true,

    creationDate: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user,
  };

  Object.keys(newJob).forEach((key) => {
    if (newJob[key] === undefined) delete newJob[key];
  });

  const ref = await addDoc(collection(db, "jobs"), newJob);

  return {
    id: ref.id,
    wo: newJob.wo,
    job: { ...newJob, id: ref.id, creationDate: new Date(), updatedAt: new Date() },
  };
};
